import React, { useEffect, useCallback } from 'react';

import {
    postMessageRN,
    runningInRNWebView,
    useNativeEventListener,
    parseCallback,
    WebToRNAction,
    EikaMessageEvent,
} from '@eika-infrastruktur/mobilbank-lib';
import { Icon, IconTypes } from '@eika/icon';

import { shouldCreateNewConsent } from '../../../actions/sbl';
import { StringKeyValue } from '../../../actions/types';
import Loader from '../../../components/Loader/Loader';
import StepButtons from '../../../components/Steps/StepButtons';
import { CompletionReducer } from '../../../reducers/completion';
import { LoaderReducer } from '../../../reducers/loader';
import { SblState } from '../../../reducers/sbl';
import { TrackingStep, trackEvent } from '../../../util/piwik-utils';

interface SblProps {
    action: {
        getOrCreateSblConsent: () => void;
        openStep: (destinationStep: number) => void;
        sblFailed: () => void;
    };
    loader: LoaderReducer;
    text: StringKeyValue;
    sbl: SblState;
    completion: CompletionReducer;
}

const LoanCompletionSbl = (props: SblProps): JSX.Element => {
    const { text, loader, sbl, action, completion } = props;
    const { customerUrl, sblStatus } = sbl;
    const { isCreatingSblConsent, createSblConsentFailed } = loader;
    const { advisorApplication } = completion.ongoing;

    useEffect(() => {
        if (advisorApplication || sblStatus === 'ACCEPTED') {
            action.openStep(3);
            return;
        }

        if (shouldCreateNewConsent(sblStatus) && !isCreatingSblConsent && !createSblConsentFailed) {
            action.getOrCreateSblConsent();
        }
    }, [action, advisorApplication, sblStatus, isCreatingSblConsent, createSblConsentFailed]);

    const useHandleSblCallback = () => {
        const onMessage = useCallback((event: EikaMessageEvent<any>) => {
            const data = parseCallback(event.data);
            if (data?.action === 'BROWSER_CALLBACK_RESULT') {
                const payload = String(data?.payload);
                if (payload.includes('/error')) {
                    action.sblFailed();
                } else {
                    action.getOrCreateSblConsent();
                }
            }
        }, []);
        useNativeEventListener(onMessage);
    };

    useHandleSblCallback();

    const goToAltinnClick = () => {
        if (customerUrl && sblStatus && ['UNOPENED', 'OPENED'].includes(sblStatus)) {
            trackEvent(TrackingStep.CompletionSbl);
            if (runningInRNWebView) {
                postMessageRN({
                    action: WebToRNAction.OPEN_BROWSER_CALLBACK,
                    payload: { uri: customerUrl, callbackResultPrefix: 'result/' },
                });
            } else {
                window.location.href = customerUrl;
            }
        } else if (sblStatus === 'ACCEPTED') action.openStep(3);
        else {
            action.sblFailed();
        }
    };

    if (isCreatingSblConsent || (shouldCreateNewConsent(sblStatus) && !createSblConsentFailed)) {
        return <Loader title={text.loadingDefault} />;
    }

    return (
        <div>
            <h2>{text.sblTitle}</h2>
            <p>{text.sblInfoText1}</p>
            <p>{text.sblInfoText2}</p>
            <StepButtons
                showNext
                nextChangeEvent={() => goToAltinnClick()}
                nextButtonName={
                    <>
                        {text.sblGoToAltinnButtonText} <Icon type={IconTypes.EKSTERN_24} />
                    </>
                }
            />
        </div>
    );
};

export default LoanCompletionSbl;
